import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { ScoreBar } from "@/components/Primitives";

export default function Reports() {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/reports/readiness").then(({ data }) => setReport(data)).catch((e) => setError(errMsg(e, "Unable to load report")));
  }, []);

  if (error) return <p data-testid="reports-error" className="text-sm text-destructive">{error}</p>;
  if (!report) return <div className="grid place-items-center py-24" data-testid="reports-loading"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-10 fade-up" data-testid="reports-page">
      <div>
        <p className="overline text-primary">job readiness report</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">
          {report.readinessScore}% · {report.verdict}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Combines assessment, lessons, exercises, troubleshooting labs, capstone milestones and mock interview performance.
        </p>
      </div>

      <div className="border border-border p-6">
        <ScoreBar value={report.readinessScore} testId="readiness-bar" />
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-6 font-mono text-xs">
          <div><p className="text-muted-foreground">assessment</p><p className="mt-1 text-base">{report.metrics.assessmentScore}</p></div>
          <div><p className="text-muted-foreground">lessons</p><p className="mt-1 text-base">{report.metrics.lessonsCompleted}/{report.metrics.lessonsPlanned}</p></div>
          <div><p className="text-muted-foreground">exercises</p><p className="mt-1 text-base">{report.metrics.exercisesSubmitted}</p></div>
          <div><p className="text-muted-foreground">timeline</p><p className="mt-1 text-base">{report.metrics.estimatedWeeks || "—"} wks</p></div>
          <div><p className="text-muted-foreground">labs</p><p className="mt-1 text-base">{report.metrics.scenariosAttempted}/{report.metrics.scenariosTotal}</p></div>
          <div><p className="text-muted-foreground">milestones</p><p className="mt-1 text-base">{report.metrics.milestonesSubmitted}/{report.metrics.milestonesTotal}</p></div>
          <div><p className="text-muted-foreground">interview</p><p className="mt-1 text-base">{report.metrics.interviewScore || "—"}</p></div>
          <div><p className="text-muted-foreground">verdict</p><p className="mt-1 text-base">{report.interviewRecommendation || "—"}</p></div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">Score composition</h2>
        <div className="mt-5 border-t border-l border-border grid grid-cols-1 sm:grid-cols-3" data-testid="report-components">
          {report.components.map((c) => (
            <div key={c.key} data-testid={`component-${c.key}`} className="border-r border-b border-border p-4">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium">{c.label}</p>
                <p className="font-mono text-xs">{c.score} · {c.weight}%</p>
              </div>
              <div className="mt-2.5"><ScoreBar value={c.score} /></div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold tracking-tight">Category bands</h2>
        <div className="mt-5 border-t border-l border-border grid grid-cols-1 sm:grid-cols-2" data-testid="report-categories">
          {report.categories.length ? report.categories.map((c) => (
            <div key={c.category} className="border-r border-b border-border p-4">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium">{c.categoryName}</p>
                <p className="font-mono text-xs">{c.score} · {c.band}</p>
              </div>
              <div className="mt-2.5"><ScoreBar value={c.score} /></div>
            </div>
          )) : <p className="p-5 text-sm text-muted-foreground border-r border-b border-border">Take the assessment to populate this report.</p>}
        </div>
      </div>

      <div className="border border-border p-6">
        <p className="overline text-muted-foreground">recommendations</p>
        <ul className="mt-3 space-y-2 text-sm" data-testid="report-recommendations">
          {report.recommendations.map((r) => (
            <li key={r} className="flex gap-2"><span className="text-primary font-mono">›</span>{r}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
